import {useCallback} from 'react';
import useWorkflowStore from '../store';
import {newNode} from '../nodes/utils';
import {v4 as uuidv4} from 'uuid';
import { Edge, Node } from '@xyflow/react';
import {normalize_workflow_nodes} from '../utils/auto_calculate_dates';

export const useImportWorkflow = () => {
  const store = useWorkflowStore();
  const {
    project_data,
    setNodes,
    setEdges,
    clearChanges,
    takeSnapshot,
  } = store;

  const setupData = useCallback(
    (nodes: Node[], edges: Edge[]) => {
      // check if start node is missing
      let start_node = nodes.find(n => n.type === 'start');
      if (!start_node) {
        start_node = newNode({
          type: 'start',
          position: {x: 0, y: 0},
          data: {start_date: project_data?.start_date || new Date()},
        });
        nodes.push(start_node);
      }
      // make sure start_date is project start date
      start_node.data = {...start_node.data, start_date: project_data?.start_date || new Date()};

      // update uuids and members
      // Create a mapping from old IDs to new IDs
      const idMapping: Record<string, string> = {};

      // First pass: generate new IDs for all nodes and store the mapping
      for (const node of nodes) {
        const oldId = node.id;
        const newId = uuidv4();
        idMapping[oldId] = newId;
        
        // Update the node's ID
        node.id = newId;
        node.data.id = newId;
        
        // Clear members array
        node.data.members = [];
      }

      // Second pass: update edge source and target IDs using the mapping
      for (const edge of edges) {
        if (idMapping[edge.source]) {
          edge.source = idMapping[edge.source];
        }
        
        if (idMapping[edge.target]) {
          edge.target = idMapping[edge.target];
        }
        
        // Generate new ID for edge as well
        edge.id = uuidv4();
      }

      const {nodes: newNodes, edges: newEdges} = normalize_workflow_nodes(nodes, edges);
      setNodes(newNodes);
      setEdges(newEdges);

      // clear dirty objects because it is newly loaded
      clearChanges();

      // marked loaded
      takeSnapshot(); //save initial state
    },
    [project_data, setNodes, setEdges, clearChanges, takeSnapshot],
  );

  return useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = event => {
        try {
          const data = JSON.parse(event.target?.result as string);
          if (data.nodes && data.edges) {
            setupData(data.nodes, data.edges);
          }
        } catch (error) {
          console.error('Error importing workflow:', error);
        }
      };
      reader.readAsText(file);
      e.target.value = ''; // Reset input
    },
    [setNodes, setEdges],
  );
};
